import { alpha, type Theme } from "@mui/material";

/**
 * The one source for a field's surface, border, and focus treatment.
 *
 * §31 already said all fields share one label treatment and one border, and the label half
 * was genuinely centralised (`FieldLabel`). The border half was not: it was re-expressed
 * inline in every field component, so the same line was written from three different tokens.
 * Measured on Settings → Company → General, where they sit side by side:
 *
 *   Name (Input, FilledInput)          1px grey[200]     rgb(207, 209, 211)
 *   Country (SelectField, Outlined)    1px primary[100]  rgb(172, 175, 180)
 *
 * Same form, same row, two different greys — and neither was "wrong" locally, which is
 * exactly why enumerating tokens per component does not hold. A field's chrome is one
 * decision; these helpers are where it lives.
 *
 * Structural note on why the mismatch hid for so long: a MUI `FilledInput` draws its border
 * on the ROOT, while an `OutlinedInput` draws it on a `<fieldset>` notched-outline CHILD. So
 * a component can look border-matched in a diff and differ on screen, and the root of an
 * outlined field reports `0px` if you measure the obvious element. Compare the rendered
 * edge, not the declaration.
 */

/**
 * Field fill — a slight grey in light, `primary[50]` in dark.
 *
 * Light was pure #ffffff, which made a field on a white card a 1.000:1 fill match with the card:
 * the border was the ONLY thing defining the control. Dark never did that — there a field is
 * #18181b on a #0f0f12 panel, a 1.080:1 step, so the fill itself says "input here".
 *
 * #f6f6f6 against a white panel measures 1.081:1, matching dark's step almost exactly. It is
 * also already `primary[50]`, so this is an existing token rather than a new grey.
 *
 * Known limit: against the #f4f5f5 PAGE this is a hair over 1:1, i.e. invisible as a fill. Fields
 * overwhelmingly sit on white cards, drawers and modals, where the step reads; a field placed
 * directly on the page background is defined by its border alone, as it was everywhere before.
 *
 * This also settles an inconsistency inside light mode itself: the date fields already rendered
 * a grey fill while the text/search fields were white, so two fields in the same Filters panel
 * disagreed about what an input looks like.
 *
 * THIS IS ALSO THE SECONDARY BUTTON'S FILL. A field and the buttons beside it in a table toolbar
 * are the same surface at the same elevation, and they did not match: the field was #18181b while
 * every button was #1f1f23, which read as the search box being a different colour from the
 * controls around it. One token now, so the row cannot drift again — see `Button.tsx`.
 */
export const fieldFill = (theme: Theme): string =>
  theme.palette.mode === "light" ? "#fafafa" : "#1a1a1e";

/**
 * DARK MODE WAS DIMMED, 2026-08-28: #1f1f23 -> #1a1a1e.
 *
 * At #1f1f23 a field sat 16 steps above the card it lives on (#0f0f12) and read as
 * RAISED — lifted off the surface, like a button. That inverts the metaphor light mode
 * uses, where #fafafa on a #ffffff card is a recess: an input is something you type INTO.
 * It also made fields the loudest thing on a form, which is backwards for a container.
 *
 * #1a1a1e keeps them clearly distinct from the card (11 steps, still legible as their own
 * surface) while dropping the lift. Not taken all the way below the card, which would be
 * the fully "correct" well — that is a larger change, because this token is deliberately
 * SHARED with buttons (see the note above on why they were unified), and buttons should
 * stay raised. Dimming both together keeps the toolbar row cohesive, which is the property
 * the shared token exists to protect; splitting them into separate well/raised tokens is
 * the real fix and is a bigger, app-wide change than this.
 */

/**
 * Rest border — `primary[100]`, the SAME token the data table outlines itself with
 * (`Table.tsx`: `borderColor: theme.palette.primary[100]`).
 *
 * Light was grey[300] while fields were WHITE on grey cards and the border did all the
 * defining. Now the fill differs from the page, so the border only has to outline a shape
 * the fill already suggests — grey[300] on top of that read as a heavy box. grey[200] was
 * the first step down from that; this is the next, and it lands on the table's own value.
 *
 * Why it matters: a search field sits directly above the table it filters, and at grey[200]
 * (#cfd1d3) it was visibly heavier than the table's #e7e7e7 — measured 1.53:1 against a white
 * fill versus the table's 1.24:1. The field read as the only outlined thing on the page. Dark
 * mode already resolved to `primary[100]`, so this only moves light, and it makes both modes
 * one value shared with the table.
 */
export const fieldBorderColor = (theme: Theme): string =>
  (theme.palette as any).primary?.[100] || (theme.palette.mode === "light" ? "#e7e7e7" : "#27272a");

/** Hover AND focus border — the same brand colour for both (§31). */
export const fieldAccentColor = (theme: Theme): string =>
  (theme.palette as any).brand?.[600] || "#7F48DF";

/**
 * The brand token BOTH rings are derived from — `brand['600-6%']`
 * (`hsla(262, 70%, 58%, 0.06)`).
 *
 * The ordering of the fallbacks does NOT matter for hue, and it is worth writing that
 * down because it looks as though it should. `hsl(262, 70%, 58%)` resolves to
 * rgb(128, 73, 223) and `brand[600]` is #7F48DF = rgb(127, 72, 223) — the same colour
 * within 1/255, confirmed against the browser's own resolution of the shipped rule
 * (`rgba(128, 73, 223, …)`). `alpha()` overwrites the alpha channel rather than
 * multiplying it, so either token yields an identical ring. The translucent one is named
 * first only because it is the canonical ring token the app already used.
 *
 * What DOES matter is that hover and focus derive from the SAME expression. That is what
 * makes them one mechanism at two strengths rather than two effects that happen to agree.
 *
 * `brand` has no light/dark split (one palette, both modes), so the ring's rgba is
 * byte-identical in light and dark — verified in the browser, both modes, same string.
 * Only the BACKDROP differs, so the perceived strength does: measured max-channel delta
 * from the surface behind it is 11/255 on hover and 22–24/255 on focus in both modes.
 */
const fieldRingBase = (theme: Theme): string =>
  (theme.palette as any).brand?.["600-6%"] ||
  (theme.palette as any).brand?.[600] ||
  "hsla(262, 70%, 58%, 0.06)";

/**
 * The two ring geometries, as numbers rather than as three hand-written `box-shadow`
 * strings. These are `px` and a 0–1 alpha, NOT `theme.spacing()` multipliers — this is a
 * layer-1 file and the two apps run different spacing bases (§29/§47), so a multiplier
 * here would render at double size in `apps/app`.
 *
 * ## A RING HAS THREE AXES, AND THE MISSING ONE WAS `blur` (2026-09-01)
 *
 * Both rings used to be `0 0 0 <spread>px` — a HARD band, no blur. That shape is the
 * reason two successive alpha raises (3%->6% hover, 6%->12% focus, both the same day)
 * produced no perceptible change, and it is worth writing down why, because "raise the
 * alpha again" is the obvious next move and it does not work.
 *
 * Measured on `/products/create`, dark mode, max-channel delta from the surface behind
 * the ring (card `rgb(15,15,18)`; the light figures are against a white card):
 *
 *                                        dark      light
 *   rest border of an UNTOUCHED field     24/255    24/255
 *   focus ring at 12% (the old value)     25/255    22/255   <-- the defect
 *   focus BORDER (brand, opaque)         205/255   182/255
 *
 * The focus ring was landing within 1/255 of the RESTING BORDER OF A FIELD NOBODY HAD
 * TOUCHED, while sitting immediately outside a border 8x its own weight. So it was not a
 * quiet signal, it was ambient chrome: exactly as loud as the thing that is always there,
 * and adjacent to something that drowns it. Doubling 6%->12% moved it from "half a rest
 * border" to "one rest border", which is why nobody could see the change.
 *
 * Alpha alone cannot fix that at a tolerable value, so the fix is `blur`. A blurred
 * shadow reads as EMITTED LIGHT: it peaks hard against the border and falls off, instead
 * of drawing a second hard-edged rectangle around the first. A hard band wide enough to
 * be seen (8px at 20%) was tested side by side and reads as a second frame competing with
 * the border, not as a halo — the soft edge is the ingredient, not more spread.
 *
 * Focus now peaks at 61/255 (dark) / 55/255 (light) — ~2.5x the rest border rather than
 * equal to it, and unmistakable in a screenshot with the old value in the same frame.
 *
 * ## THE EXTENT BUDGET IS 27px, NOT 52 — measure to the next LABEL, not the next FIELD
 *
 * Fields on this form sit ~52px apart bottom-edge to top-edge, which sounds like ample
 * room. It is not the constraint: the next field's LABEL sits in that gap, and the real
 * clearance from a field's bottom edge to the next label's text box is **27px** (measured,
 * SKU -> Serial Number). A ring is budgeted against 27, not 52.
 *
 * `blur + spread` is the outermost reach, where opacity has already decayed to nothing;
 * focus at 10+2 reaches 12px with a visible extent nearer 10, leaving ~15px clear of the
 * label below. Do not raise these past roughly `blur + spread <= 16` without re-measuring
 * that gap on a real form.
 *
 * ## THE ORDERING INVARIANTS
 *
 * Focus must dominate hover on EVERY axis, so a later tune to one number cannot collapse
 * the progression: alpha 2:1 (0.30/0.15), blur 10 > 6, spread 2 > 1. The 2:1 alpha ratio
 * is the load-bearing one — it is what encodes rest < hover < focus — and it survives this
 * change unchanged from the previous pair.
 *
 * FOCUS and ERROR deliberately share one entry. The error ring's own docblock declares it
 * "the same geometry and same opacity as `fieldFocusRing`, error colour instead of brand"
 * — one mechanism in two colours — but it was RESTATING those numbers, so the 2026-09-01
 * focus change (6%/3px -> 12%/4px) silently left the error ring at the old values. That
 * would have made an INVALID field's focus ring quieter than a valid one's: the same
 * inversion the error docblock explicitly rejects, arrived at from the other direction and
 * with nothing to catch it, because agreement-by-coincidence looks identical to agreement
 * by design right up until one side moves.
 *
 * So the parity is structural now. Changing `FIELD_FOCUS_RING` moves both, and the only
 * way to diverge them is to deliberately write a second constant — which is a decision a
 * reader can see, rather than an omission they cannot.
 */
// 2026-09-02: focus reach 12 -> 9. The number that matters to the eye is
// `blur + spread` (the outermost reach), not `blur` alone — 10+2 read as "12px
// and too much" at the same moment 6+1 read as "6 and fine". Taken out of blur
// rather than spread so the ring keeps its density near the border; dropping
// spread to 1 instead would have flattened it into hover's shape at a slightly
// larger radius, which is the one thing the two-ring system must not do.
// Ratio after the change: reach 9 vs 7 — a narrower gap than 12 vs 7, so if
// focus and hover now read as too similar, the next move is alpha (0.30/0.15),
// NOT reach.
const FIELD_FOCUS_RING = { blur: 7, spread: 2, alpha: 0.3 } as const;
const FIELD_HOVER_RING = { blur: 6, spread: 1, alpha: 0.15 } as const;

/**
 * One place that turns a ring's three numbers into a `box-shadow`, so the three builders
 * below cannot disagree about the ORDER of the values.
 *
 * `box-shadow: <x> <y> <blur> <spread> <color>` — blur before spread. Writing the pair the
 * other way round is silent: `0 0 2px 10px` is a legal shadow, it just renders a 10px band
 * with a 2px feather instead of a 2px ring with a 10px bloom, which looks like "the glow is
 * too chunky" rather than like a bug.
 */
const ringShadow = (
  ring: { blur: number; spread: number },
  color: string,
): string => `0 0 ${ring.blur}px ${ring.spread}px ${color}`;

/**
 * The soft brand glow that focus adds on top of the accent border — 30%, 10px blur,
 * 2px spread.
 *
 * REBUILT 2026-09-01 from 12%/4px hard, which was itself that morning's raise from
 * 6%/3px. See the long note on `FIELD_FOCUS_RING` for the measurement that condemned
 * both: at 12% the ring composited to 25/255 against the card while an untouched
 * field's RESTING border sits at 24/255, so the ring was pixel-for-pixel as loud as
 * ambient chrome, immediately outside a border 8x its weight.
 *
 * The lever that moved it was `blur`, not alpha. Two alpha raises in one day changed
 * nothing perceptible because a hard band adjacent to a full-opacity border reads as
 * part of the border — "a 5px border whose outer 4px are faded" — rather than as a
 * halo around it. Blur makes it read as emitted light, and concentrates the strength
 * AT the edge where the eye is already looking instead of spending it on a wide
 * uniform band. A hard 8px/20% alternative was rendered side by side and rejected: it
 * is visible, but as a second rectangle competing with the border.
 */
export const fieldFocusRing = (theme: Theme): string =>
  ringShadow(FIELD_FOCUS_RING, alpha(fieldRingBase(theme), FIELD_FOCUS_RING.alpha));

/**
 * The soft brand ring HOVER adds — a lighter preview of `fieldFocusRing`, never as
 * loud as it: half the alpha (6% vs 12%) AND a tighter 3px spread (vs 4px), so a
 * hovered-but-unfocused field reads as "about to be interactive" while focus still
 * unambiguously wins as the stronger signal (measured requirement: hover must stay
 * visibly softer than focus, and the two must never be mistaken for each other).
 *
 * REBUILT 2026-09-01, 3%/2px -> 6%/3px -> 15% at 6px blur / 1px spread. At 3% the hover
 * ring composited to ~5–6/255 against the field's own fill, and even the intermediate 6%
 * only reached ~12/255 — HALF the weight of a resting border, i.e. within the noise of
 * display gamma and ambient light. A signal nobody perceives is not a subtle signal, it
 * is an absent one. At 15% it composites to 31/255 (dark) / 27/255 (light).
 *
 * That is a deliberately modest number, and hover can afford it in a way focus could not:
 * a hovered field ALREADY carries the full-opacity brand border (205/255), so the ring is
 * a supporting cue rather than the whole message. Focus had the same border and still
 * needed the glow, because the glow is the only thing distinguishing the two states.
 *
 * The 2:1 alpha ratio to focus is preserved exactly, so this is a translation of the whole
 * scale upward rather than a change to the relationship between the two states — and hover
 * is now narrower on BOTH geometry axes too (blur 6 vs 10, spread 1 vs 2), so the
 * progression cannot collapse if a later tune moves one number.
 *
 * Derived from the SAME token as focus via `alpha()` rather than a second hand-picked
 * colour — `alpha()` replaces only the alpha channel, so this can never drift to a
 * different hue than focus, only a fainter one. This is also why every field's hover
 * ring reads as "the same mechanism, quieter" instead of a second, unrelated effect.
 *
 * Before this, NO field in the app had a hover ring at all — hover changed only the
 * border colour (`fieldAccentColor`), everywhere it changed anything. Confirmed with
 * a real pointer hover (not a synthetic DOM event — `:hover` does not respond to
 * `dispatchEvent`), across `Input`, `SelectField`, `DatePickerField` and the Ask
 * composer: `boxShadow` computed to `none` on hover in every one of them, while focus
 * already carried `fieldFocusRing`. The border-only hover treatment itself WAS taking
 * effect (verified via `element.matches(':hover')` plus computed style) — the gap was
 * the missing ring, not a broken hover rule.
 */
export const fieldHoverRing = (theme: Theme): string =>
  ringShadow(FIELD_HOVER_RING, alpha(fieldRingBase(theme), FIELD_HOVER_RING.alpha));

/**
 * INVALID border — the error colour, standing in for the accent in every state a valid
 * field would tint brand (rest, hover, focus).
 *
 * This exists because the error state was simply ABSENT from the system. `@repo/ui`'s
 * `Input` had no `&.Mui-error` rule at all: its `error` prop only tinted `Input`'s own
 * `helperText`, which the platform `TextField` never passes (it renders its own
 * `FormHelperText` below). Measured on an errored SKU field, the border computed to
 * rgb(39,39,42) — byte-identical to the rest state. An invalid field and a valid one were
 * pixel-identical apart from red text underneath.
 *
 * Read from the theme rather than hardcoded, because the two apps disagree: `@repo/ui`'s
 * palette declares no `error` key at all, so platform resolves MUI's default #d32f2f,
 * while `apps/app` declares its own #ff2c2c. A literal here would be wrong in one of them.
 */
export const fieldErrorColor = (theme: Theme): string =>
  theme.palette.error?.main || "#d32f2f";

/**
 * The soft ring an INVALID field gets on focus — same geometry and same opacity as
 * `fieldFocusRing`, error colour instead of brand.
 *
 * The alternative considered and rejected was letting an errored field fall through to the
 * brand ring on focus. That inverts the message: the field would turn brand-purple — the
 * app's "this is fine / this is the active thing" colour — at exactly the moment the user
 * has clicked in to FIX it. Red persists through focus for the same reason it persists
 * through hover.
 *
 * Geometry and alpha are READ FROM `FIELD_FOCUS_RING`, not restated here. Making the error
 * ring heavier OR lighter than the focus ring would make the two read as different
 * mechanisms rather than one mechanism in two colours, and a restated literal is exactly
 * how that drift happens — see the note on `FIELD_FOCUS_RING` for the instance that
 * prompted this, where a focus-ring change left the error ring behind at the old numbers.
 *
 * The one thing that does NOT transfer: the alpha is applied to `error.main`, a fully
 * opaque red, whereas the brand rings apply it to an already-translucent token. The
 * resulting alpha is identical (`alpha()` overwrites rather than multiplies), but red at
 * a given alpha is perceptually louder than purple at the same alpha — reds sit further
 * from both the light and the dark field fills. That is acceptable and arguably correct
 * here (an invalid field should not be quieter than a valid one), but it means the two
 * are matched by CONSTRUCTION, not by measured perceptual weight. If the error ring ever
 * reads as shouting, tune it with its own constant and say so, rather than dropping the
 * focus ring to meet it.
 *
 * ⚠️ That caveat got bigger on 2026-09-01 — the shared alpha went 12% -> 30% and gained a
 * 10px blur, so any gap between red and purple is now applied to a signal 2.5x stronger
 * over a larger area — so it was CHECKED rather than left as a worry. Measured on a live
 * errored+focused SKU field, `/products/create`, dark mode:
 *
 *   error ring  rgba(244,67,54,0.30) 0 0 10px 2px  -> composites to rgb(84,31,29)  69/255
 *   focus ring  rgba(128,73,223,0.30) 0 0 10px 2px -> composites to rgb(49,32,79)  61/255
 *
 * 69 against 61 — red lands ~13% louder than brand, which is the direction this docblock
 * argues for (an invalid field should not be quieter than a valid one) and nowhere near
 * loud enough to shout. Confirmed by eye in the same pass. The shared constant stands.
 *
 * One correction worth keeping, because it would mislead anyone redoing this arithmetic:
 * `error.main` does NOT resolve to the `#d32f2f` fallback below on the platform's dark
 * theme — it resolves to **rgb(244,67,54)** (#f44336), a materially lighter red. A
 * calculation run against the fallback literal understates the rendered figure by ~20/255.
 * Read the value off the running app, not off this file's default.
 */
export const fieldErrorRing = (theme: Theme): string =>
  ringShadow(
    FIELD_FOCUS_RING,
    alpha(theme.palette.error?.main || "#d32f2f", FIELD_FOCUS_RING.alpha),
  );

/**
 * A field's TEXT SIZE — 16px — and it belongs on the field's ROOT, not on its inner input.
 *
 * The distinction is the whole reason this is a token. `Input` set it on the root; the shared
 * `Autocomplete` set it only on the inner `<input>` via `inputProps`. Measured in the analytics
 * Filters drawer, light mode, with Location sitting directly under three `Input`-backed search
 * fields:
 *
 *   input font-size    16px    vs 16px   — agreed, which is why this hid
 *   ROOT font-size     16px    vs 20px
 *   line-height        23px    vs 28.75px
 *
 * 20px is `typography.body1.fontSize`, which MUI's `InputBase` spreads onto the root. Patching
 * only the inner input leaves the root at body1, and MUI's `line-height: 1.4375em` is declared on
 * the ROOT — where `em` resolves against 20px, not 16 — and inherits down as a computed LENGTH.
 * So the glyphs matched and everything derived from the root's font-size did not.
 *
 * That is the failure mode a per-component literal produces: an override that looks applied
 * because the one property you checked agreed. Set this on the root and the derived values follow.
 */
export const FIELD_FONT_SIZE = 16;

/**
 * Placeholder opacity — 0.65. See the long note at `Input`'s `::placeholder` rule for why this
 * number rather than MUI's default: it halves the placeholder→value contrast jump (0.5→1.0
 * becomes 0.65→1.0) while keeping a placeholder clearly subordinate to a real value.
 *
 * Here because a field that does NOT set it does not fall back to something close — it falls
 * back to MUI's own, which computes to `opacity: 1` over `rgba(0,0,0,0.42)`. Measured against a
 * sibling at 0.65: effective alpha 0.42 vs 0.273, a 1.54× contrast difference at identical
 * font-size, weight and family. Identical glyphs at 1.5× the contrast read as heavier — which
 * is what "the placeholder font doesn't match" describes, and no amount of weight-fiddling
 * touches it, because no weight differs.
 */
export const FIELD_PLACEHOLDER_OPACITY = 0.65;

/**
 * Corner radius for a field: 8px.
 *
 * Deliberately SMALLER than the 12px of the containers fields sit in (cards, menu papers,
 * the data grid). Concentric nesting — an inner corner needs a tighter radius than the
 * corner around it, or the two curves read as fighting. Do not "align" this to 12.
 */
export const FIELD_RADIUS = 8;

/**
 * The height of a single-line field: 49px — the shared `Input` at rest (12px padding, a 23px
 * line, 1px borders). Every select-like control matches it so a filter row or a form reads
 * as one band. Measured 2026-10-14 (VRG-703): `SelectField` was hardcoded to 47 and the
 * platform `Search` grew to 50 from its 24px magnifier, so Analytics → Website's Visitor
 * history row showed three heights (47 / 50 / 50 / 47) in four fields.
 */
export const FIELD_HEIGHT = 49;

/**
 * The height of a control that sits in a TABLE TOOLBAR — Download, Views, Filter, Sort,
 * Layout, and the clear-X buttons attached to Filter/Sort.
 *
 * 49, and the reason is FIELD-HEIGHT PARITY, not importance: the toolbar shares its row
 * with the 49px search field, and those sit side by side where a height difference is read
 * instantly. This is the same argument `Header` makes for why the PAGE HEADER is 40 — the
 * header's actions have no field beside them to match.
 *
 * These were briefly dropped to 40 along with the header. That was wrong: it left every
 * toolbar button 9px shorter than the search bar immediately to its left.
 */
export const TOOLBAR_CONTROL_HEIGHT = 49;

/**
 * The height of a control that sits in the PAGE HEADER band — 40, the Button default.
 *
 * Distinct from TOOLBAR_CONTROL_HEIGHT above and deliberately so: the two bands answer to
 * different neighbours. A header control's neighbour is the title; a toolbar control's is
 * a 49px field. Page-header BUTTONS get this from `Header` itself; this token is for the
 * non-button controls that share that band (e.g. SegmentedTabs).
 */
export const CONTROL_HEIGHT = 40;

/**
 * Suppress Chrome's autofill background — the steel-blue fill it paints over a
 * prefilled input, which overrides the field's own colours and, on a dark theme,
 * reads as a broken control rather than a filled one.
 *
 * **Why an `sx` fragment and not just the `transition` trick.** The widely-copied
 * fix is `transition: background-color 600000s`, which defers the autofill paint
 * effectively forever. It only works when there IS a transition to defer — i.e.
 * when the fill arrives after first paint. When Chrome autofills **on page load**
 * (a saved password, restored on navigation) the background is part of the initial
 * style computation, nothing transitions, and the blue lands anyway.
 *
 * `-webkit-background-clip: text` is what actually holds: it clips the UA's
 * background to the glyphs, so there is no block fill left to see, on load or
 * later. The transition is kept as belt-and-braces for the post-paint case.
 *
 * Extracted 2026-09-03. It had been hand-rolled twice — SignInPage's email field
 * had the robust form, `AuthPasswordField` had only the transition — so the email
 * box rendered correctly while the password box beside it went blue. Two copies of
 * one treatment is exactly the drift working-conventions §36 says to extract on.
 */
export const fieldAutofillResetSx = (theme: Theme) => ({
  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active':
    {
      WebkitBoxShadow: 'none',
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      WebkitTextFillColor: theme.palette.text.primary,
      caretColor: theme.palette.text.primary,
      transition: 'background-color 600000s 0s, color 600000s 0s',
    },
});
